import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import * as crmBackend from '../services/crmBackendService';
import EstimateDetailDialog from '../components/EstimateDetailDialog';
import InvoiceDetailDialog from '../components/InvoiceDetailDialog';
import ProcessPaymentDialog from '../components/ProcessPaymentDialog';
import TechnicianMultiSelect from '../components/TechnicianMultiSelect';
import { Phone, Mail, MapPin, ArrowLeft, Plus } from 'lucide-react';

const { TIME_WINDOWS } = crmBackend;

// Phase 2B — summary tile order/labels, direct passthrough from the 2A `counts` object.
const SUMMARY_TILES = [
  { key: 'estimates', label: 'Estimates' },
  { key: 'work_orders', label: 'Work Orders' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'payments', label: 'Payments' },
  { key: 'service_locations', label: 'Locations' },
];

// Filter tab row — the owner's explicit spec (6 tabs, no separate Communications tab here;
// Communications gets its own always-visible section below, §4.6 of the design doc).
const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'estimate', label: 'Estimates' },
  { key: 'work_order', label: 'Work Orders' },
  { key: 'invoice', label: 'Invoices' },
  { key: 'payment', label: 'Payments' },
  { key: 'note', label: 'Notes' },
];

const TYPE_ICONS = {
  estimate: '📋',
  invoice: '🧾',
  work_order: '🔧',
  payment: '💰',
  note: '📝',
  communication: '✉️',
};

function timelineIcon(entry) {
  if (entry.type === 'communication') {
    const medium = (entry.communication_medium || entry.communication_type || '').toLowerCase();
    if (medium.includes('phone') || medium.includes('call')) return '📞';
    if (medium.includes('sms') || medium.includes('text')) return '💬';
    return '✉️';
  }
  return TYPE_ICONS[entry.type] || '•';
}

function formatDateHeader(dayKey) {
  if (!dayKey || dayKey === 'undated') return 'No date';
  const d = new Date(`${dayKey}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dayKey;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function formatCustomerSince(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

// Whether a timeline entry has a genuine destination — per the owner's explicit
// constraint, entries are clickable ONLY when a real detail screen exists for
// them. See docs/DESIGN-customer-history-2B-2026-08-14.md §4.9.
function isTimelineEntryClickable(entry) {
  if (entry.type === 'estimate' || entry.type === 'invoice' || entry.type === 'work_order') return true;
  if (entry.type === 'payment') return !!entry.related_invoice;
  if (entry.type === 'note') {
    return !!entry.reference_name
      && (entry.reference_doctype === 'Quotation' || entry.reference_doctype === 'Sales Invoice');
  }
  return false; // communication (2C, future) and any unrecognized type render as plain text
}

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [contact, setContact] = useState(location.state?.contact || null);

  // Phase 2B — unified history: { data: {estimates, invoices, work_orders,
  // payments, service_locations, communications, notes, timeline}, counts: {...} }
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  // "+ Create Estimate" — existing flow, unchanged, demoted to a corner action.
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ address: '', date: '', time: '8AM-11AM', technician: '', budget: '' });
  const [saving, setSaving] = useState(false);

  // Estimate detail dialog (reused component) — same state shape Dashboard.jsx owns.
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [estimateDetailOpen, setEstimateDetailOpen] = useState(false);
  const [estimateEditForm, setEstimateEditForm] = useState({});
  const [estimateSaving, setEstimateSaving] = useState(false);
  const [sendingEstimate, setSendingEstimate] = useState(false);
  const [completingJob, setCompletingJob] = useState(false);

  // Invoice detail dialog (reused component).
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetailOpen, setInvoiceDetailOpen] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);

  // Record Payment dialog — mirrors Dashboard.jsx's own payOpen/payInvoice/payForm/paySaving
  // so InvoiceDetailDialog's "Record Payment" button works identically from history.
  const [payOpen, setPayOpen] = useState(false);
  const [payInvoice, setPayInvoice] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', mode: 'Cash', reference_no: '' });
  const [paySaving, setPaySaving] = useState(false);

  // Process Payment (live card charge) — same ProcessPaymentDialog Dashboard.jsx uses.
  const [chargeInvoice, setChargeInvoice] = useState(null);

  useEffect(() => {
    if (!contact) {
      crmBackend.getCustomerDetail(id)
        .then(res => setContact(res.data))
        .catch(() => toast({ title: 'Could not load contact', variant: 'destructive' }));
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHistory = () => {
    if (!contact) return;
    const isLead = contact.status === 'Lead';
    setHistoryLoading(true);
    setHistoryError(false);
    crmBackend.getContactHistory(contact.id, isLead ? 'lead' : 'customer')
      .then(res => setHistory(res))
      .catch(() => setHistoryError(true))
      .finally(() => setHistoryLoading(false));
  };

  useEffect(() => {
    if (!contact) return;
    setForm(f => ({
      ...f,
      address: [contact.address_line1, contact.city, contact.state].filter(Boolean).join(', '),
    }));
    loadHistory();
  }, [contact]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async () => {
    if (!form.date) { toast({ title: 'Please pick a date', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const isLead = contact.status === 'Lead';
      const payload = {
        address: form.address,
        scheduled_date: form.date,
        scheduled_time: form.time,
        technician: form.technician,
        budget: form.budget ? Number(form.budget) : undefined,
      };
      if (isLead) await crmBackend.createEstimateFromLead(contact.id, payload);
      else await crmBackend.createEstimateFromCustomer(contact.id, payload);
      toast({ title: 'Estimate created' });
      setCreateOpen(false);
      loadHistory();
    } catch (err) {
      toast({ title: 'Failed to create estimate', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Same status-color vocabulary Dashboard.jsx uses (Lead/Customer badge colors plus
  // the estimate-approval and invoice statuses the extracted dialogs need). Kept as a
  // local duplicate here — extracting it into a shared module was out of scope for 2B.
  const getStatusColor = (status) => {
    const colors = {
      'Active': 'bg-green-100 text-green-700',
      'Lead': 'bg-blue-100 text-blue-700',
      'Customer': 'bg-green-100 text-green-700',
      'Paid': 'bg-green-100 text-green-700',
      'Pending': 'bg-yellow-100 text-yellow-700',
      'Overdue': 'bg-red-100 text-red-700',
      'Sent': 'bg-blue-100 text-blue-700',
      'Draft': 'bg-gray-100 text-gray-700',
      'Sent for Approval': 'bg-yellow-100 text-yellow-700',
      'Approved': 'bg-green-100 text-green-700',
      'Declined': 'bg-red-100 text-red-700',
      'Cancelled': 'bg-red-100 text-red-700',
      'Replied': 'bg-yellow-100 text-yellow-700',
      'Lost': 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // ── Estimate dialog actions (same behavior as Dashboard.jsx's own handlers,
  //    refreshing this page's history fetch instead of Dashboard's global lists) ──

  const openEstimateDetail = async (name) => {
    if (!name) return;
    try {
      const result = await crmBackend.getEstimateDetail(name);
      const doc = result.data;
      setSelectedEstimate(doc);
      setEstimateEditForm({
        custom_appointment_status: doc.custom_appointment_status || '',
        custom_scheduled_date: doc.custom_scheduled_date || '',
        custom_scheduled_time: doc.custom_scheduled_time || '',
        custom_technician: doc.custom_technician || '',
        custom_job_status: crmBackend.normalizeJobStatus(doc.custom_job_status),
      });
      setEstimateDetailOpen(true);
    } catch (err) {
      toast({ title: 'Could not load estimate', description: err.message, variant: 'destructive' });
    }
  };

  const refreshSelectedEstimate = async () => {
    if (!selectedEstimate?.name) return;
    try {
      const result = await crmBackend.getEstimateDetail(selectedEstimate.name);
      if (result.data) setSelectedEstimate(result.data);
    } catch { /* non-fatal — dialog list already reflects the save */ }
  };

  const saveEstimate = async () => {
    if (!selectedEstimate) return;
    setEstimateSaving(true);
    try {
      await crmBackend.updateEstimate(selectedEstimate.name, estimateEditForm);
      toast({ title: 'Estimate updated' });
      setEstimateDetailOpen(false);
      loadHistory();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setEstimateSaving(false);
    }
  };

  const handleSendEstimate = async () => {
    if (!selectedEstimate) return;
    setSendingEstimate(true);
    try {
      await crmBackend.sendEstimateToCustomer(selectedEstimate.name);
      setSelectedEstimate(e => ({ ...e, custom_approval_status: 'Sent for Approval' }));
      toast({ title: 'Estimate sent', description: `${selectedEstimate.name} emailed for customer approval.` });
      loadHistory();
    } catch (err) {
      toast({ title: 'Could not send estimate', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSendingEstimate(false);
    }
  };

  const handleCompleteEstimateJob = async () => {
    if (!selectedEstimate) return;
    setCompletingJob(true);
    try {
      await crmBackend.updateJobStatus(selectedEstimate.name, 'Completed');
      let woId = null;
      try {
        const existing = await crmBackend.getWorkOrderDetail(selectedEstimate.name);
        woId = existing?.data?.wo_id || null;
      } catch {
        // No Work Order yet — expected for a job completed straight from the office.
      }
      if (!woId) {
        const created = await crmBackend.createWorkOrder(selectedEstimate.name, selectedEstimate.custom_technician || '', 'office');
        woId = created.wo_id;
      }
      const invResult = await crmBackend.generateWOInvoice(woId, selectedEstimate.custom_technician || '');
      setEstimateEditForm(f => ({ ...f, custom_job_status: 'Completed' }));
      toast({
        title: 'Job completed',
        description: invResult.invoice_name ? `Invoice ${invResult.invoice_name} created.` : 'Job marked complete.',
      });
      loadHistory();
    } catch (err) {
      toast({ title: 'Could not complete job', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setCompletingJob(false);
    }
  };

  // ── Invoice dialog actions ──

  const openInvoiceDetail = async (name) => {
    if (!name) return;
    try {
      const result = await crmBackend.getInvoiceDetail(name);
      setSelectedInvoice(result.data);
      setInvoiceDetailOpen(true);
    } catch (err) {
      toast({ title: 'Could not load invoice', description: err.message, variant: 'destructive' });
    }
  };

  const handleSendInvoice = async () => {
    if (!selectedInvoice) return;
    setSendingInvoice(true);
    try {
      await crmBackend.sendInvoiceToCustomer(selectedInvoice.name);
      toast({ title: 'Invoice sent', description: `${selectedInvoice.name} emailed to the customer for payment.` });
    } catch (err) {
      toast({ title: 'Could not send invoice', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSendingInvoice(false);
    }
  };

  const handleOpenPayment = (inv) => {
    setPayInvoice(inv);
    setPayForm({ amount: String(inv.outstanding_amount || inv.grand_total || ''), mode: 'Cash', reference_no: '' });
    setInvoiceDetailOpen(false);
    setPayOpen(true);
  };

  const handleSavePayment = async () => {
    if (!payInvoice) return;
    setPaySaving(true);
    try {
      await crmBackend.payInvoice(payInvoice.name, {
        amount: parseFloat(payForm.amount) || 0,
        mode_of_payment: payForm.mode,
        reference_no: payForm.reference_no,
      });
      toast({ title: 'Payment recorded' });
      setPayOpen(false);
      loadHistory();
    } catch (err) {
      toast({ title: 'Payment failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setPaySaving(false);
    }
  };

  // ── Timeline click routing (design doc §4.9) ──

  const handleTimelineClick = (entry) => {
    if (!isTimelineEntryClickable(entry)) return;
    if (entry.type === 'estimate') { openEstimateDetail(entry.id || entry.name); return; }
    if (entry.type === 'invoice') { openInvoiceDetail(entry.id || entry.name); return; }
    if (entry.type === 'work_order') { navigate(`/tech/workorder/${entry.id}`); return; }
    if (entry.type === 'payment') { openInvoiceDetail(entry.related_invoice); return; }
    if (entry.type === 'note') {
      if (entry.reference_doctype === 'Quotation') { openEstimateDetail(entry.reference_name); return; }
      if (entry.reference_doctype === 'Sales Invoice') { openInvoiceDetail(entry.reference_name); return; }
    }
  };

  const timelineEntries = history?.data?.timeline || [];
  const filteredTimeline = useMemo(
    () => (activeFilter === 'all' ? timelineEntries : timelineEntries.filter(e => e.type === activeFilter)),
    [timelineEntries, activeFilter]
  );

  // Group into day buckets, preserving the 2A endpoint's own newest-first sort —
  // no re-sorting here (design doc §4.3).
  const groupedTimeline = useMemo(() => {
    const buckets = [];
    let currentKey = null;
    let currentBucket = null;
    for (const entry of filteredTimeline) {
      const key = (entry.date || '').slice(0, 10) || 'undated';
      if (key !== currentKey) {
        currentKey = key;
        currentBucket = { key, entries: [] };
        buckets.push(currentBucket);
      }
      currentBucket.entries.push(entry);
    }
    return buckets;
  }, [filteredTimeline]);

  const counts = history?.counts || {};
  const serviceLocations = history?.data?.service_locations || [];
  const communications = history?.data?.communications || [];

  const commsByCategory = useMemo(() => {
    const buckets = { call: [], sms: [], email: [] };
    communications.forEach(c => {
      const medium = (c.communication_medium || c.communication_type || '').toLowerCase();
      if (medium.includes('phone') || medium.includes('call')) buckets.call.push(c);
      else if (medium.includes('sms') || medium.includes('text')) buckets.sms.push(c);
      else buckets.email.push(c);
    });
    return buckets;
  }, [communications]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back */}
        <button
          onClick={() => navigate('/dashboard', { state: { addContact: false } })}
          className="flex items-center text-sm text-gray-500 hover:text-gray-800 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Contacts
        </button>

        {/* Header card */}
        <Card className="mb-6">
          <CardContent className="p-6">
            {contact ? (
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-4">
                  <Avatar className="h-16 w-16">
                    <AvatarFallback className="bg-blue-500 text-white text-2xl">
                      {(contact.name || 'C').substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">{contact.name}</h1>
                    {contact.company && <p className="text-gray-500">{contact.company}</p>}
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={getStatusColor(contact.status)}>
                        {contact.status || 'Active'}
                      </Badge>
                      {formatCustomerSince(contact.customer_since) && (
                        <span className="text-xs text-gray-400">
                          Customer since {formatCustomerSince(contact.customer_since)}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-600">
                      {contact.phone && (
                        <a href={`tel:${contact.phone}`} className="flex items-center hover:text-green-700">
                          <Phone className="w-4 h-4 mr-1" />{contact.phone}
                        </a>
                      )}
                      {contact.email && (
                        <a href={`mailto:${contact.email}`} className="flex items-center hover:text-blue-700">
                          <Mail className="w-4 h-4 mr-1" />{contact.email}
                        </a>
                      )}
                      {(contact.address_line1 || contact.city) && (
                        <span className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          {[contact.address_line1, contact.city, contact.state, contact.postal_code].filter(Boolean).join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => setCreateOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-2" /> Create Estimate
                </Button>
              </div>
            ) : (
              <div className="text-sm text-gray-400">Loading…</div>
            )}
          </CardContent>
        </Card>

        {/* Summary counts row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-6">
          {SUMMARY_TILES.map(tile => (
            <Card key={tile.key}>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{counts[tile.key] ?? 0}</p>
                <p className="text-xs text-gray-500 mt-1">{tile.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Recent Activity — unified timeline */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Recent Activity</CardTitle>
            <div className="flex flex-wrap gap-2 pt-2">
              {FILTER_TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveFilter(tab.key)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    activeFilter === tab.key
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 text-gray-600 hover:border-blue-400'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            {historyLoading && <p className="text-sm text-gray-400">Loading history…</p>}
            {!historyLoading && historyError && (
              <p className="text-sm text-red-500">Could not load history. Try refreshing the page.</p>
            )}
            {!historyLoading && !historyError && groupedTimeline.length === 0 && (
              <p className="text-sm text-gray-400">No activity yet.</p>
            )}
            {!historyLoading && !historyError && groupedTimeline.map(group => (
              <div key={group.key} className="mb-4 last:mb-0">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 border-b pb-1">
                  {formatDateHeader(group.key)}
                </p>
                {group.entries.map((entry, idx) => {
                  const clickable = isTimelineEntryClickable(entry);
                  const rowClasses = `flex items-center justify-between py-2 ${
                    clickable ? 'cursor-pointer hover:bg-gray-50' : ''
                  }`;
                  return (
                    <div
                      key={`${entry.type}-${entry.id || entry.name || idx}`}
                      className={rowClasses}
                      onClick={clickable ? () => handleTimelineClick(entry) : undefined}
                      role={clickable ? 'button' : undefined}
                      tabIndex={clickable ? 0 : undefined}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-lg">{timelineIcon(entry)}</span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {entry.title || entry.id || 'Untitled'}
                          </p>
                          {entry.description && (
                            <p className="text-xs text-gray-400 truncate max-w-xs">{entry.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {entry.amount != null && entry.amount !== '' && (
                          <span className="text-sm font-medium text-gray-700">
                            {crmBackend.formatCurrency(entry.amount)}
                          </span>
                        )}
                        {entry.status && (
                          <Badge className={`text-xs ${getStatusColor(entry.status)}`}>{entry.status}</Badge>
                        )}
                        {clickable && <span className="text-gray-300">›</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Service Locations — separate section, not part of the timeline (design §4.4) */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Service Locations</CardTitle>
          </CardHeader>
          <CardContent>
            {serviceLocations.length === 0 && (
              <p className="text-sm text-gray-400">No service locations on file.</p>
            )}
            {serviceLocations.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {serviceLocations.map((loc, idx) => (
                  <div key={loc.name || idx} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-gray-900 text-sm">{loc.location_name || 'Service Location'}</p>
                      <Badge className={loc.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                        {loc.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {[loc.address_line1, loc.address_line2].filter(Boolean).join(', ')}
                    </p>
                    <p className="text-xs text-gray-500">
                      {[loc.city, loc.state, loc.zip].filter(Boolean).join(', ')}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Communications — always three categories, per-category empty state (design §4.6) */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Communications</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { key: 'call', icon: '📞', label: 'Calls' },
              { key: 'sms', icon: '💬', label: 'SMS' },
              { key: 'email', icon: '✉️', label: 'Email' },
            ].map(cat => (
              <div key={cat.key} className="flex items-center justify-between border-b last:border-0 pb-2 last:pb-0">
                <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <span>{cat.icon}</span> {cat.label}
                </span>
                <span className="text-xs text-gray-400">
                  {commsByCategory[cat.key].length === 0
                    ? 'No communication history recorded'
                    : `${commsByCategory[cat.key].length} record(s)`}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Create Estimate dialog — existing flow, unchanged */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Estimate — {contact?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Address</Label>
              <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Service address" />
            </div>
            <div>
              <Label>Date *</Label>
              <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>
            <div>
              <Label>Time Window</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {TIME_WINDOWS.map(w => (
                  <button key={w} type="button"
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${form.time === w ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-700 hover:border-blue-400'}`}
                    onClick={() => setForm(f => ({ ...f, time: w }))}
                  >{w}</button>
                ))}
              </div>
            </div>
            <div>
              <TechnicianMultiSelect
                value={form.technician}
                onChange={(val) => setForm((f) => ({ ...f, technician: val }))}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
                {saving ? 'Creating…' : 'Create Estimate'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Estimate / Invoice detail — same extracted components Dashboard.jsx uses */}
      <EstimateDetailDialog
        open={estimateDetailOpen}
        onOpenChange={setEstimateDetailOpen}
        estimate={selectedEstimate}
        editForm={estimateEditForm}
        onEditFormChange={setEstimateEditForm}
        sending={sendingEstimate}
        onSend={handleSendEstimate}
        completing={completingJob}
        onCompleteJob={handleCompleteEstimateJob}
        saving={estimateSaving}
        onSave={saveEstimate}
        getStatusColor={getStatusColor}
        onItemsChanged={refreshSelectedEstimate}
      />

      <InvoiceDetailDialog
        open={invoiceDetailOpen}
        onOpenChange={setInvoiceDetailOpen}
        invoice={selectedInvoice}
        sending={sendingInvoice}
        onSend={handleSendInvoice}
        onRecordPayment={handleOpenPayment}
        onProcessPayment={(inv) => setChargeInvoice(inv)}
        getStatusColor={getStatusColor}
      />

      {/* Record Payment dialog — mirrors Dashboard.jsx's own, needed so InvoiceDetailDialog's
          "Record Payment" button is fully functional when reused from history. */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          {payInvoice && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">{payInvoice.name} — {payInvoice.customer_name || payInvoice.customer}</p>
              <div>
                <Label>Amount ($)</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={payForm.amount}
                  onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))}
                />
              </div>
              <div>
                <Label>Payment Method</Label>
                <select
                  className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
                  value={payForm.mode}
                  onChange={(e) => setPayForm((f) => ({ ...f, mode: e.target.value }))}
                >
                  <option>Cash</option>
                  <option>Check</option>
                  <option>Credit Card</option>
                  <option>Zelle</option>
                </select>
              </div>
              <div>
                <Label>Reference / Check No.</Label>
                <Input
                  placeholder={
                    payForm.mode === 'Check' ? 'Check number'
                      : payForm.mode === 'Credit Card' ? 'Auth code or last 4 digits'
                      : payForm.mode === 'Zelle' ? 'Confirmation number'
                      : 'Optional'
                  }
                  value={payForm.reference_no}
                  onChange={(e) => setPayForm((f) => ({ ...f, reference_no: e.target.value }))}
                />
                {payForm.mode === 'Credit Card' && (
                  <p className="mt-1 text-xs text-gray-500">
                    Records a card payment already taken on a terminal. To actually charge
                    a card now, close this and use <strong>Process Payment</strong> instead.
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button onClick={handleSavePayment} disabled={paySaving} className="bg-green-600 hover:bg-green-700">
              {paySaving ? 'Saving…' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Process Payment — live Authorize.net card charge, same component Dashboard.jsx uses */}
      {chargeInvoice && (
        <ProcessPaymentDialog
          invoice={chargeInvoice}
          surface="Admin App"
          onClose={() => setChargeInvoice(null)}
          onSuccess={() => loadHistory()}
        />
      )}
    </div>
  );
}
